import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectorRef, ElementRef } from '@angular/core';
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexTooltip,
  ApexStroke,
  ApexLegend,
  ApexMarkers,
  ApexYAxis,
} from 'ng-apexcharts';
import { DaterangepickerDirective, LocaleConfig } from 'ngx-daterangepicker-material';
import { Subject } from 'rxjs';
import { LocaleConfigDate } from 'src/app/constants/date-config.const';
import { ORDER_ANALYTICS } from 'src/app/constants/endpoint.const';
import { BaseService } from 'src/app/services/base.service';
import { DataAnalyticResponseType } from 'src/app/types/response/data-analytic.response.type';
import { HostListener } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ResponseCustomType } from 'src/app/types/response/response.type';
import { OrderAnalyticsResponseType } from 'src/app/types/response/order-analytics-response.type';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
};

interface ApexTooltipContext {
  globals: {
    series: number[][];
    lastXAxis: {
      categories: string[];
    };
  };
}

@Component({
  selector: 'app-chart-analytics',
  standalone: false,
  templateUrl: './chart-analytics.component.html',
  styleUrl: './chart-analytics.component.scss'
})
export class ChartAnalyticsComponent {
  loading = true;
  @ViewChild(DaterangepickerDirective, { static: false }) datePicker!: DaterangepickerDirective;
  @ViewChild('chart', { static: false }) chart!: ChartComponent;
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef<HTMLDivElement>;

  private ro!: ResizeObserver;
  private resizeTimeout?: ReturnType<typeof setTimeout>;

  @Output() filterResult = new EventEmitter<void>();
  setlocale: LocaleConfig = LocaleConfigDate;

  public chartOptions: Partial<ChartOptions> = {
    markers: {
      size: 0,
      hover: { size: 0 },
      discrete: [],
    },
    dataLabels: { enabled: false },
  };

  @Input() data: DataAnalyticResponseType["chart"] = [];
  dataValues: DataAnalyticResponseType["chart"] = [];
  private subject$ = new Subject<void>();
  public legend: ApexLegend = {
    position: 'bottom',
    horizontalAlign: 'center',
    show: true,
    markers: {
      shape: 'line',
    },
  };

  deliveredData: Array<Number> = [];
  inProcessData: Array<Number> = [];
  returnedData: Array<Number> = [];

  params: { month: string } = {
    month: new Date().getFullYear() + '-' + (new Date().getMonth() + 1).toString().padStart(2, '0'),
  };

  @HostListener('window:resize')
  onResize() {
    if (this.chart) {
      setTimeout(() => {
        this.chart.updateOptions(this.chartOptions, true, true);
      }, 200);
    }
  }

  constructor(
    private baseService: BaseService,
    private cdr: ChangeDetectorRef,
    private message: NzMessageService,
  ) { }

  ngAfterViewInit() {
    this.setupResizeObserver();

    // Passive event listener
    if (this.chartContainer) {
      const el = this.chartContainer.nativeElement;
      el.addEventListener('wheel', (e) => {}, { passive: false });
      el.addEventListener('touchstart', (e) => {}, { passive: false });
      el.addEventListener('touchmove', (e) => {}, { passive: false });
    }

    window.addEventListener('resize', () => {
      if (this.chart) {
        this.chart.updateOptions(this.chartOptions, true, true);
      }
    });
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.loading = false;
    }, 500);
  }

  private setupResizeObserver() {
    if (!('ResizeObserver' in window) || !this.chartContainer) {
      return;
    }

    this.ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          try {
            if (this.chart) {
              this.chart.updateOptions(this.chartOptions, true, true);
            } else {
              window.dispatchEvent(new Event('resize'));
            }
            this.cdr.detectChanges();
          } catch (err) {
            console.warn('Error while resizing chart:', err);
          }
        }, 120);
      }
    });

    this.ro.observe(this.chartContainer.nativeElement);
  }

  private ensureObserver() {
    if (!this.ro && this.chartContainer) {
      this.setupResizeObserver();
    }
  }

  chartOption() {
    let categories = this.dataValues.map((item) => item.date);

    const [year, month] = (this.params.month || new Date().toISOString().slice(0, 7))
      .split('-')
      .map(Number);

    const daysInMonth = new Date(year, month, 0).getDate();
    const generatedDates: string[] = [];
    for (let day = 2; day <= daysInMonth; day += 2) {
      generatedDates.push(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    }

    if (!categories.length) {
      categories = generatedDates;
      this.deliveredData = new Array(generatedDates.length).fill(0);
      this.inProcessData = new Array(generatedDates.length).fill(0);
      this.returnedData = new Array(generatedDates.length).fill(0);
    } else {
      const dateSet = new Set([...generatedDates, ...categories]);
      const mergedDates = Array.from(dateSet).sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime()
      );

      const dataMap = this.dataValues.reduce((acc: { [key: string]: { delivered: number; inProcess: number; returned: number } }, item: { date: string; delivered: number; in_process: number; returned: number }) => {
        acc[item.date] = {
          delivered: item.delivered ?? 0,
          inProcess: item.in_process ?? 0,
          returned: item.returned ?? 0,
        };
        return acc;
      }, {});

      this.deliveredData = mergedDates.map((d) => dataMap[d]?.delivered ?? 0);
      this.inProcessData = mergedDates.map((d) => dataMap[d]?.inProcess ?? 0);
      this.returnedData = mergedDates.map((d) => dataMap[d]?.returned ?? 0);

      categories = mergedDates;
    }

    this.chartOptions = {
      series: [
        { color: '#8979FF', name: 'Delivered', data: this.deliveredData },
        { color: '#FF928A', name: 'In Process', data: this.inProcessData },
        { color: '#3CC3DF', name: 'Returned', data: this.returnedData },
      ],
      chart: {
        height: 350,
        width: '100%',
        type: 'area',
        toolbar: { show: false },
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'straight' },
      markers: { size: 0, hover: { size: 0 }, discrete: [] },
      yaxis: {
        forceNiceScale: true,
        tickAmount: 6,
        labels: {
          formatter(value: number) {
            return Number.isInteger(value) ? value.toString() : '';
          },
        },
      },
      xaxis: {
        categories,
        type: 'category',
        labels: {
          formatter(value: string) {
            const date = new Date(value);
            return date.getDate().toString();
          },
        },
      },
      tooltip: {
        enabled: true,
        custom: ({ dataPointIndex, w }: { dataPointIndex: number, w: ApexTooltipContext }) => {
          const delivered = w.globals.series[0][dataPointIndex] as number;
          const inProcess = w.globals.series[1][dataPointIndex] as number;
          const returned = w.globals.series[2][dataPointIndex] as number;
          const date = new Date(w.globals.lastXAxis.categories[dataPointIndex]);
          const dateTime = date.toLocaleDateString('us-US', { day: 'numeric', month: 'long', year: 'numeric' });

          const label = (color: string, text: string, value: number) =>
            value !== undefined
              ? `<div class="d-flex gap-1 align-items-center">
                  <span class="w-4 h-1" style="background-color: ${color}"></span>
                  ${value} ${text}
                </div>`
              : '';

          return `
            <div class="p-2">
              ${label('#8979FF', 'Delivered', delivered)}
              ${label('#FF928A', 'In Process', inProcess)}
              ${label('#3CC3DF', 'Returned', returned)}
              <small>${dateTime}</small>
            </div>`;
        },
      },
    };

    setTimeout(() => {
      if (this.chart) {
        this.chart.updateOptions(this.chartOptions, true, true);
      }

      window.dispatchEvent(new Event('resize'));
      this.cdr.detectChanges();
    }, 100);
  }

  changeMonthPicker(e: { year: number; monthIndex: number; }) {
    this.params.month = e.year + '-' + (e.monthIndex + 1).toString().padStart(2, '0');
    this.getDataChart();
  }

  getDataChart() {
    const params = {
      include: 'chart',
      start_date: this.params.month,
      end_date: this.params.month,
    };
    this.baseService.getData(ORDER_ANALYTICS, params).subscribe({
      next: (res: ResponseCustomType<OrderAnalyticsResponseType>) => {
        this.dataValues = res.data.chart;
        this.deliveredData = this.dataValues.map((i) => i.delivered);
        this.inProcessData = this.dataValues.map((i) => i.in_process);
        this.returnedData = this.dataValues.map((i) => i.returned);
        this.chartOption();
        setTimeout(() => this.ensureObserver(), 200);

        setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
      },
      error: () => {
        this.message.error('Failed to load chart data');},
    });
  }
}
